import { type FC, useState } from 'react';

import { ProfileUI } from '@crehana/compass.layouts';
import { cn } from '@crehana/compass.ui/v2';
import { useTranslation } from '@crehana/i18n';
import ArrowLeft2 from '@crehana/icons/ArrowLeft2';
import CrehanaLogoIso from '@crehana/icons/CrehanaLogoIso';
import QuestionOutline from '@crehana/icons/QuestionOutline';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Col, Container, colors, Row } from '@crehana/ui';
import { useRouter } from 'next/router';

import useAdminDashboard from '../../hooks/useAdminDashboard';
import { HelpDrawer } from './components';
import {
  BackButton,
  BeautyButton,
  HelpWrapper,
  WrapperActions,
  WrapperHeader,
  WrapperLogo,
} from './HeaderLight.styles';
import type { HeaderLight } from './types';

const { black, primaryMain, white, secondaryMain } = colors;

const mountedStyle =
  'tw-transition-opacity tw-ease-in tw-opacity-100 tw-duration-400';
const unmountedStyle =
  'tw-transition-opacity tw-ease-out tw-fill-mode-forwards tw-opacity-0 tw-duration-400';

const Header: FC<React.PropsWithChildren<HeaderLight>> = ({
  backLabel,
  backUrl,
  isDark = false,
  title,
  helpCenterHidden,
  helpCenterArticles,
  onClick,
  canVersionSimple,
  customHeight,
  urlToRedirectHelpButton,
}) => {
  const { asPath } = useRouter();
  const { t } = useTranslation();
  const { slug } = useAdminDashboard();
  const ENABLE_USER_ASSIGNMENT = true;
  const toggleVersionHeader = canVersionSimple ?? ENABLE_USER_ASSIGNMENT;

  const [activeBackButton, setActiveBackButton] = useState(false);
  const [isDrawerHelpOpen, setIsDrawerHelpOpen] = useState(false);

  const onHandleOpenDrawerOrRedireccionToURL = () => {
    if (urlToRedirectHelpButton) {
      window.open(urlToRedirectHelpButton);
      return;
    }
    setIsDrawerHelpOpen(true);
  };

  return (
    <WrapperHeader isDark={isDark} height={customHeight}>
      <WrapperActions>
        {onClick ? (
          <BackButton
            isDark={isDark}
            onMouseEnter={() => setActiveBackButton(true)}
            onMouseLeave={() => setActiveBackButton(false)}
            onClick={onClick}
          >
            <ArrowLeft2
              size={18}
              color={isDark ? secondaryMain : primaryMain}
            />
          </BackButton>
        ) : (
          <BackButton
            isDark={isDark}
            onMouseEnter={() => setActiveBackButton(true)}
            onMouseLeave={() => setActiveBackButton(false)}
            href={backUrl}
          >
            <ArrowLeft2
              size={18}
              color={isDark ? secondaryMain : primaryMain}
            />
          </BackButton>
        )}
        {!toggleVersionHeader && (
          <WrapperLogo>
            <p
              className={cn(
                'tw-font-link1 !tw-text-1.6 tw-absolute dark:tw-text-neutral-light-100 tw-text-neutral-light-800',
                activeBackButton ? mountedStyle : unmountedStyle,
              )}
            >
              {backLabel}
            </p>
            <div className={!activeBackButton ? mountedStyle : unmountedStyle}>
              <CrehanaLogoIso
                className="tw-cursor-pointer"
                color={isDark ? white : primaryMain}
                secondaryColor={black}
                size={18}
              />
            </div>
          </WrapperLogo>
        )}
      </WrapperActions>
      <Container className="tw-flex tw-items-center">
        <Row className="tw-flex tw-flex-row tw-w-full">
          <Col xs={12} md={12} className="tw-relative tw-text-center">
            <div
              className={cn(
                'dark:tw-text-neutral-light-100 tw-text-neutral-light-800',
                toggleVersionHeader ? 'tw-font-h5' : 'tw-font-subtitle4',
                { 'tw-font-normal': toggleVersionHeader },
              )}
            >
              {title}
            </div>
            {!helpCenterHidden && (
              <HelpWrapper className="tw-absolute">
                <BeautyButton isDark={isDark}>
                  {/* TODO: talk with the code owner to fix this code so we can enable this rule again */}
                  <button
                    type="button"
                    className="tw-flex tw-items-center tw-cursor-pointer"
                    onClick={onHandleOpenDrawerOrRedireccionToURL}
                  >
                    <QuestionOutline
                      size={20}
                      color="currentColor"
                      className="tw-hidden sm:tw-flex"
                    />
                    <p className="tw-font-subtitle5 tw-ml-0.8 tw-mr-2">
                      {t('HEADER_HELP')}
                    </p>
                  </button>
                </BeautyButton>
                {!toggleVersionHeader && (
                  <div className="tw-hidden sm:tw-block tw-text-left">
                    <ProfileUI currentUrl={asPath} organizationSlug={slug} />
                  </div>
                )}
              </HelpWrapper>
            )}
          </Col>
        </Row>
      </Container>

      <HelpDrawer
        isDark={isDark}
        open={isDrawerHelpOpen}
        onDismiss={() => setIsDrawerHelpOpen(false)}
        articles={helpCenterArticles}
      />
    </WrapperHeader>
  );
};

export default Header;
